/**
 * Pure selectors over AppData. None of these mutate or touch storage —
 * they're plain functions you can call from a component, a hook, or a
 * test. Where ordering matters (recent transactions, etc.), the result
 * is sorted but the input is left untouched.
 */

import type { AppData, Category, Transaction } from './types';
import { fromIsoDate, isBetweenInclusive, startOfDay, toIsoDate } from '../lib/date';
import { getPayPeriod } from '../lib/payPeriod';

/** Map of categoryId -> Category for O(1) lookups. */
export function buildCategoryMap(
  categories: Category[],
): Map<string, Category> {
  const m = new Map<string, Category>();
  for (const c of categories) m.set(c.id, c);
  return m;
}

/** Recent transactions, newest first, capped to `limit`. */
export function selectRecentTransactions(
  data: AppData,
  limit = 5,
): Transaction[] {
  return [...data.transactions]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, limit);
}

/** All transactions, newest first. */
export function selectAllTransactions(data: AppData): Transaction[] {
  return [...data.transactions].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
}

/**
 * Active spread transactions — i.e. spread purchases whose effective
 * end date is on or after `today`. Paid-off rows (where spreadEndedOn
 * is in the past) drop out automatically. Sorted by start date asc so
 * the oldest "still amortizing" purchase comes first.
 */
export function selectSpreadTransactions(
  data: AppData,
  today: Date = new Date(),
): Transaction[] {
  const todayMs = startOfDay(today).getTime();
  return data.transactions
    .filter((t) => {
      const days = t.spreadDays ?? 0;
      if (days <= 1) return false;
      const txStartMs = startOfDay(fromIsoDate(t.date)).getTime();
      // Natural end (in ms) for this spread.
      const naturalEndMs = txStartMs + (days - 1) * 24 * 60 * 60 * 1000;
      const effectiveEndMs = t.spreadEndedOn
        ? Math.min(
            naturalEndMs,
            startOfDay(fromIsoDate(t.spreadEndedOn)).getTime(),
          )
        : naturalEndMs;
      return effectiveEndMs >= todayMs;
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Transactions on a given local calendar day. */
export function selectTransactionsOnDate(
  data: AppData,
  date: Date,
): Transaction[] {
  const iso = toIsoDate(date);
  return data.transactions.filter((t) => t.date === iso);
}

/** Transactions falling in the current pay period. */
export function selectTransactionsInCurrentPeriod(
  data: AppData,
  today: Date = new Date(),
): Transaction[] {
  const period = getPayPeriod(data.profile, today);
  return data.transactions.filter((t) =>
    isBetweenInclusive(fromIsoDate(t.date), period.start, period.end),
  );
}

/** Transactions grouped by ISO date, newest date first. */
export function groupTransactionsByDate(
  txs: Transaction[],
): Array<{ date: string; items: Transaction[]; total: number }> {
  const groups = new Map<string, Transaction[]>();
  for (const t of txs) {
    const arr = groups.get(t.date) ?? [];
    arr.push(t);
    groups.set(t.date, arr);
  }
  return [...groups.entries()]
    .map(([date, items]) => {
      const total = items.reduce(
        (a, t) => a + (t.type === 'expense' ? -t.amount : t.amount),
        0,
      );
      return {
        date,
        items: items.sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
        total,
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));
}

/** Active scheduled items, ordered by next due date ascending. */
export function selectActiveScheduledItems(data: AppData) {
  return data.scheduledItems
    .filter((i) => i.active)
    .sort((a, b) => a.nextDate.localeCompare(b.nextDate));
}

/** Goals ordered by deadline ascending; goals without a deadline come last. */
export function selectGoalsByDeadline(data: AppData) {
  const FAR = '9999-12-31';
  return [...data.goals].sort((a, b) =>
    (a.deadline ?? FAR).localeCompare(b.deadline ?? FAR),
  );
}

export function selectExpenseCategories(data: AppData): Category[] {
  return data.categories
    .filter((c) => c.type === 'expense')
    .sort((a, b) => a.order - b.order);
}

export function selectIncomeCategories(data: AppData): Category[] {
  return data.categories
    .filter((c) => c.type === 'income')
    .sort((a, b) => a.order - b.order);
}

export function selectUnlockedAchievements(data: AppData) {
  return data.achievements.filter((a) => !!a.unlockedAt);
}

/** Whether the user is in an active Pro state (purchased OR within 7-day trial). */
export function isProActive(data: AppData, now: Date = new Date()): boolean {
  if (data.proPurchased) return true;
  if (!data.proTrial) return false;
  const started = new Date(data.proTrial.started).getTime();
  if (!Number.isFinite(started)) return false;
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  return now.getTime() - started < sevenDaysMs;
}
